/**
 * <im-description-clamp> — "Read more" / "Read less" on the PDP description.
 *
 * Markup: snippets/product-information-blocks.liquid, `description` branch.
 * Styles: the "PDP blocks" block at the end of assets/improvemint-brand.css.
 *
 * THE CLAMP IS CSS, NOT THIS FILE. `.js im-description-clamp:not([data-expanded])`
 * line-clamps the body from the first paint, because <html> gets its `js` class
 * synchronously in <head> (snippets/js-variables.liquid). Clamping from here
 * instead would paint the whole description and then snap it shut — exactly
 * the layout jump this has to avoid. This file only decides whether the toggle
 * is needed, and flips the state.
 *
 * WHETHER THE TOGGLE SHOWS is a two-stage decision, for the same reason:
 *   1. Liquid guesses from the description's length and block count and writes
 *      data-guess, so CSS can reserve the button's line before any script runs.
 *   2. This file measures — scrollHeight against clientHeight while clamped —
 *      and writes data-overflow="true|false", which overrides the guess.
 * The guess is right for the common cases (a long Best Buy description, a
 * one-liner), so the correction in step 2 is rarely visible.
 *
 * A CUSTOM ELEMENT, not a query-on-load script, because the product block is
 * not stable DOM: product-info.js swaps the whole <product-info> on a combined
 * listing, handleBlocksPosition() moves block innerHTML between the info column
 * and below-media on breakpoint changes, and quick view injects the markup
 * after load. connectedCallback runs in all three; a DOMContentLoaded query
 * would run in none of them. Every lookup is scoped to `this`, so two product
 * sections on one page (main product + featured product) never cross-wire.
 */
if (!customElements.get('im-description-clamp')) {
  class ImDescriptionClamp extends HTMLElement {
    connectedCallback() {
      this.body = this.querySelector('[data-im-desc-body]');
      this.toggle = this.querySelector('[data-im-desc-toggle]');
      if (!this.body || !this.toggle) return;

      this.abort = new AbortController();
      const { signal } = this.abort;

      this.toggle.addEventListener('click', () => this.setExpanded(!this.expanded, true), { signal });

      // A link in line 4+ of a collapsed description is still in the tab order
      // (overflow clipping does not remove it). Tabbing onto it would put focus
      // somewhere nobody can see, so open the description instead.
      this.body.addEventListener(
        'focusin',
        (event) => {
          if (!this.expanded && this.dataset.overflow === 'true' && !this.isVisible(event.target)) {
            this.setExpanded(true, false);
          }
        },
        { signal },
      );

      // Width is what changes the line count. Height is ignored on purpose: the
      // element's own height changes every time it expands, and re-measuring on
      // that would be measuring our own output.
      this.lastWidth = -1;
      this.observer = new ResizeObserver((entries) => {
        const width = Math.round(entries[0].contentRect.width);
        if (width === this.lastWidth) return;
        this.lastWidth = width;
        this.measure();
      });
      this.observer.observe(this);

      // The web font can land after the first measurement and re-wrap the text.
      document.fonts?.ready.then(() => this.isConnected && this.measure());
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.observer?.disconnect();
    }

    get expanded() {
      return this.hasAttribute('data-expanded');
    }

    /**
     * Does the description run past the clamp?
     *
     * Only meaningful while clamped, so when expanded the clamp is re-applied
     * for the read and removed again. Both style writes and the read happen in
     * one synchronous task, so the browser never paints the intermediate state
     * and no ResizeObserver entry is produced (sizes are compared at the next
     * rendering step, by which point nothing has changed).
     */
    measure() {
      const wasExpanded = this.expanded;
      if (wasExpanded) this.removeAttribute('data-expanded');
      // 1px of slack: fractional line-heights round differently in the two
      // properties and would otherwise flag a description of exactly 3 lines.
      const overflowing = this.body.scrollHeight - this.body.clientHeight > 1;
      if (wasExpanded) this.setAttribute('data-expanded', '');

      this.dataset.overflow = String(overflowing);
      // Nothing is hidden, so there is nothing to collapse either — drop back to
      // the resting state rather than leave a "Read less" that does nothing.
      if (!overflowing && wasExpanded) this.setExpanded(false, false);
    }

    setExpanded(expand, fromToggle) {
      this.toggleAttribute('data-expanded', expand);
      this.toggle.setAttribute('aria-expanded', String(expand));
      this.toggle.textContent = expand ? this.toggle.dataset.labelLess : this.toggle.dataset.labelMore;

      // Collapsing a long description from its bottom can leave the reader
      // scrolled far past the product info. Bring the top of the description
      // back into view — but only when it has actually left the viewport, so a
      // short collapse near the top of the page does not move anything.
      if (!expand && fromToggle && this.getBoundingClientRect().top < 0) {
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.scrollIntoView({ block: 'start', behavior: reduce ? 'auto' : 'smooth' });
      }
    }

    isVisible(element) {
      const box = this.body.getBoundingClientRect();
      const rect = element.getBoundingClientRect();
      return rect.top >= box.top && rect.bottom <= box.bottom;
    }
  }

  customElements.define('im-description-clamp', ImDescriptionClamp);
}
